import { Fragment, useState } from 'react'
import { Building2, Laptop, Plus, Trash2 } from 'lucide-react'

import { isUuid } from '@/shared/lib/entity'
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogTitle } from '@/ui/alert-dialog'
import { DialogFooter } from '@/ui/dialog'
import { Button } from '@/ui/button'
import { Frame, FrameCard, FrameConnector } from '@/ui/frame'
import { Input } from '@/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/select'
import { TextField } from '@/ui/text-field'
import { useEntity } from '../../lib/entity-queries'
import { readSelectorGui, selectorExpression, type SelectorGui, type SelectorCondition, type SelectorField, ENTITY_SELECTOR_FIELDS, SELECTOR_COMPARISONS } from './policy-selector-model'

type Condition = SelectorCondition & { id: string }
type SelectorMode = 'all' | 'matching' | 'expression'
const identityComparisons = SELECTOR_COMPARISONS.filter((comparison) =>
  comparison.value === 'equals' || comparison.value === 'not-equals',
)
const maximumSelectorLength = 4096

function newCondition(fields: readonly SelectorField[]): Condition {
  return { id: crypto.randomUUID(), field: fields[0]?.value ?? 'target.id', comparison: 'contains', value: '' }
}

export function PolicySelector({ entityId, value, gui, disabled, onChange, targetFields = ENTITY_SELECTOR_FIELDS, targetLabel = 'entities' }: {
  entityId: string; value: string; gui?: unknown; disabled: boolean
  targetFields?: readonly SelectorField[]; targetLabel?: string
  onChange: (expression: string, valid: boolean, gui: SelectorGui | null) => void
}) {
  const fields = targetFields
  const entity = useEntity(entityId)
  const [initialGui] = useState(() => readSelectorGui(gui, value, fields))
  const [mode, setMode] = useState<SelectorMode>(() => {
    if (!value) return 'all'
    return initialGui?.mode ?? 'expression'
  })
  const [confirmMatching, setConfirmMatching] = useState(false)
  const [groups, setGroups] = useState<Condition[][]>(() =>
    initialGui?.mode === 'matching'
      ? initialGui.groups.map((group) => group.map((condition) => ({ ...condition, id: crypto.randomUUID() })))
      : [],
  )

  function changeGroups(next: Condition[][]) {
    setGroups(next)
    const expression = selectorExpression(next, fields)
    const complete = next.length > 0 && next.every((group) =>
      group.every((condition) => condition.field === 'target.id'
        ? isUuid(condition.value.trim())
        : condition.value.trim().length > 0),
    )
    onChange(expression, complete && expression.length <= maximumSelectorLength, {
      version: 1,
      mode: 'matching',
      groups: next.map((group) => group.map(({ field, comparison, value }) => ({ field, comparison, value }))),
    })
  }

  function changeMode(next: SelectorMode) {
    const replacesExpression = mode === 'expression' && next === 'matching'
      && value.trim().length > 0 && selectorExpression(groups, fields) !== value
    if (replacesExpression) {
      setConfirmMatching(true)
      return
    }
    applyMode(next)
  }

  function applyMode(next: SelectorMode) {
    setMode(next)
    if (next === 'all') onChange('', true, null)
    if (next === 'matching') changeGroups(groups.length ? groups : [[newCondition(fields)]])
    if (next === 'expression') onChange(value, value.length <= maximumSelectorLength, { version: 1, mode: 'expression' })
  }

  function updateCondition(id: string, changes: Partial<Condition>) {
    const next = groups.map((group) => group.map((condition) => {
      if (condition.id !== id) return condition
      const updated = { ...condition, ...changes }
      if (updated.field === 'target.id' && updated.comparison !== 'equals' && updated.comparison !== 'not-equals') {
        updated.comparison = 'equals'
      }
      return updated
    }))
    changeGroups(next)
  }

  function removeCondition(id: string) {
    const next = groups
      .map((group) => group.filter((condition) => condition.id !== id))
      .filter((group) => group.length > 0)
    changeGroups(next)
  }

  function addCondition(groupIndex: number) {
    const next = groups.map((group, index) =>
      index === groupIndex ? [...group, newCondition(fields)] : group,
    )
    changeGroups(next)
  }

  return (
    <section className="flex flex-col gap-4" aria-labelledby="policy-scope-heading">
      <h2 id="policy-scope-heading" className="text-sm font-medium">3. Scope and selector</h2>
      <Frame title="Policy scope" description="The hierarchy where this policy may be applied.">
        <FrameCard className="flex items-center gap-3 p-4">
          <Building2 aria-hidden className="text-muted-foreground size-4 shrink-0" />
          <div className="flex flex-col gap-0.5 text-xs">
            <p className="font-medium">{entity.data?.name ?? 'Selected entity'} and all descendants</p>
            <p className="text-muted-foreground">Based on the selected entity.</p>
          </div>
        </FrameCard>
      </Frame>
      <Frame title="Policy selector" description={`Choose which ${targetLabel} within the scope this policy applies to. By default, all ${targetLabel} match.`}>
        <FrameCard className="flex flex-col gap-4 p-4">
          <SelectorChoice label="Policy selector" value={mode} disabled={disabled} options={[
            { value: 'all', label: `All ${targetLabel} in scope` },
            { value: 'matching', label: `Matching ${targetLabel}` },
            { value: 'expression', label: 'Advanced expression' },
          ]} onChange={(next) => {
            if (next === 'all' || next === 'matching' || next === 'expression') changeMode(next)
          }} icon />
          {mode === 'matching' && (
            <div className="bg-muted/50 border-border flex flex-col gap-1.5 rounded-xl border p-1.5">
              {groups.map((group, groupIndex) => (
                <Fragment key={group[0]?.id}>
                  {groupIndex > 0 && <FrameConnector>OR</FrameConnector>}
                  <FrameCard className="flex flex-col items-start gap-2 p-4" role="group" aria-label={`Group ${groupIndex + 1}`}>
                    {group.map((condition, conditionIndex) => {
                      const label = `Group ${groupIndex + 1} condition ${conditionIndex + 1}`
                      return (
                        <div key={condition.id} className="border-border flex max-w-full flex-wrap items-center rounded-lg border shadow-xs">
                          <span className="bg-muted/50 text-muted-foreground px-2.5 py-2 text-xs">{conditionIndex === 0 ? 'When' : 'And'}</span>
                          <SelectorChoice label={`${label} property`} value={condition.field} options={fields} disabled={disabled} onChange={(field) => updateCondition(condition.id, { field })} />
                          <SelectorChoice label={`${label} comparison`} value={condition.comparison} options={condition.field === 'target.id' ? identityComparisons : SELECTOR_COMPARISONS} disabled={disabled} onChange={(comparison) => updateCondition(condition.id, { comparison })} />
                          <Input aria-label={`${label} value`} className="w-44 rounded-none border-0 border-x shadow-none" placeholder={condition.field === 'target.id' ? 'Entity UUID' : 'e.g. Acme'} value={condition.value} required disabled={disabled} onChange={(event) => updateCondition(condition.id, { value: event.target.value })} />
                          <Button type="button" variant="ghost" size="icon-sm" aria-label={`Remove ${label.toLowerCase()}`} disabled={disabled} onClick={() => removeCondition(condition.id)}><Trash2 aria-hidden /></Button>
                        </div>
                      )
                    })}
                    <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={() => addCondition(groupIndex)}><Plus aria-hidden />And</Button>
                  </FrameCard>
                </Fragment>
              ))}
              <Button type="button" variant="outline" size="icon-sm" aria-label="Add alternative group (OR)" disabled={disabled} onClick={() => changeGroups([...groups, [newCondition(fields)]])}><Plus aria-hidden /></Button>
              {groups.length === 0 && <p className="text-muted-foreground px-2 text-xs">Add a condition to match {targetLabel}.</p>}
              {value.length > maximumSelectorLength && <p role="alert" className="text-destructive text-xs">The selector is too long. Remove some conditions.</p>}
            </div>
          )}
          {mode === 'expression' && <TextField name="selectorExpression" label="Selector expression (optional)" kind="text" hint={`An expression over ${fields.map((field) => field.value).join(', ')}. Leave blank to match all ${targetLabel}.`} value={value} disabled={disabled} onChange={(expression) => onChange(expression, expression.length <= maximumSelectorLength, { version: 1, mode: 'expression' })} onBlur={() => undefined} />}
        </FrameCard>
      </Frame>
      <AlertDialog open={confirmMatching} onOpenChange={setConfirmMatching}>
        <AlertDialogContent>
          <AlertDialogTitle>Replace advanced expression?</AlertDialogTitle>
          <AlertDialogDescription>Switching to matching conditions replaces the advanced expression.</AlertDialogDescription>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmMatching(false)}>Cancel</Button>
            <Button type="button" onClick={() => {
              setConfirmMatching(false)
              applyMode('matching')
            }}>Use matching conditions</Button>
          </DialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}

function SelectorChoice<Value extends string>({ label, value, options, disabled, onChange, icon = false }: {
  label: string; value: Value; options: readonly { value: Value; label: string }[]; disabled: boolean
  onChange: (value: Value) => void; icon?: boolean
}) {
  return (
    <Select value={value} disabled={disabled} onValueChange={(next) => { if (next !== null) onChange(next) }}>
      <SelectTrigger aria-label={label} className={icon ? 'w-fit self-start' : 'w-auto rounded-none border-0 bg-transparent shadow-none'}>
        {icon && <Laptop aria-hidden className="size-3.5" />}
        <SelectValue>{options.find((option) => option.value === value)?.label}</SelectValue>
      </SelectTrigger>
      <SelectContent>{options.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
    </Select>
  )
}
